<script setup>
import { ref, computed, watch } from "vue";
import { useBookingStore } from "@stores/BookingStore";
import CourseSelection from "@components/CourseSelection.vue";
import {
  calculateFinalPrice,
  calculateTotalPrice,
  calculateClinicFees,
} from "@app/helpers";

// Define Props
const props = defineProps({
  patient: Number,
  type: String,
  opened: {
    type: Boolean,
    default: false,
  },
  clinic: {
    type: Object,
    required: true,
  },
  vaccinations: {
    type: Array,
    required: false,
  },
});

// Reactive Variables
const bookingStore = useBookingStore();
const isOpen = ref(props.opened);

// Methods/Functions
const toggleState = () => {
  isOpen.value = !isOpen.value;
};

// Calculate the total price when vaccinations are updated.
watch(
  props.vaccinations,
  () => {
    if (bookingStore.serviceType !== "vaccinations") {
      return;
    }

    props.clinic.finalPrice = calculateFinalPrice(props.clinic);
    props.clinic.totalPrice = calculateTotalPrice(props.clinic);
    props.clinic.totalFees = calculateClinicFees(props.clinic.totalPrice);
  },
  { deep: true, immediate: true }
);
</script>

<template>
  <article class="px-20 sm:px-30 py-13 border border-grey-border rounded-3xl">
    <header class="flex justify-between items-center">
      <h2 class="font-medium text-grey capitalize">
        {{ type }} {{ patient + 1 }}
      </h2>

      <i
        v-if="!isOpen"
        class="text-20 cursor-pointer ri-arrow-down-s-line"
        @click="toggleState"></i>
      <i
        v-if="isOpen"
        class="text-20 cursor-pointer ri-arrow-up-s-line"
        @click="toggleState"></i>
    </header>

    <div class="max-xs:max-w-[320px] overflow-auto">
      <table
        v-show="isOpen"
        class="p-20 rounded-xl w-full text-left"
        cellpadding="10">
        <thead>
          <tr>
            <th></th>
            <th class="font-normal">
              Single dose <br />
              <span class="text-12 text-grey/[0.5]">Select dose</span>
            </th>
            <th class="font-normal">
              Full course <br />
              <span class="text-12 text-grey/[0.5]">Select dose</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <CourseSelection
            v-for="(vacc, index) in vaccinations"
            :key="index"
            :vaccination="vacc"
            :clinic="clinic"
            :type="type"
            :patient="patient"
            :class="{
              'border-grey-border sm:border-b':
                index !== vaccinations.length - 1,
            }" />
        </tbody>
      </table>
    </div>
  </article>
</template>

<style scoped lang="scss"></style>
